import { InvoicePaymentStatus } from '../models/invoice-payment-status';
import React, { Suspense, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '../components/CustomTable';
import ErrorBoundary from '../components/ErrorBoundary';
import { NoDataComponent } from '../components/NoDataComponent';
import { PaginationComponent } from '../components/Pagination';
import {
    EditProps,
    Invoice,
    InvoiceFilter,
    InvoiceTableInnerProps,
    TableInvoiceComponentProps,
} from '../models/table-models';
import { deleteDraftInvoiceSale, EmailRecipientItem, emailSaleInvoice, emailSaleInvoices, getSaleEmailRecipient, useGetSales, } from '../utils/clients-hooks';
import { useInvoiceData } from '../utils/data-hooks';
import { SalesTableHeaders, sentSalesTableHeaders } from '../utils/Enums/TableHeaders';
import { ErrorComponent } from '../components/ErrorComponent';
import { InvoiceView } from './InvoiceView';
import { resourceCache } from 'use-async-resource';
import { transformDateToString, transformPriceToString, getCurrencySymbol } from '../utils/helper-functions';
import { Role } from '../utils/Enums/Roles';
import { fetchMapBranches } from '../utils/dashboard-hooks';
import { CustomToastContent } from '../components/CustomToastContent';
import { toast } from 'react-toastify';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { showBackendError } from '../utils/backend-error';
import { CustomButton } from '../components/CustomButton';
import { EditDocument } from "../components/AddInvoicesToDocument";
import { SendInvoiceEmailModal } from "../components/ResendEmailModal";
import dateFormat from "dateformat";
import { date } from "../utils/Enums/Common";
import { ExportSales } from "../exports/ExportSales";
import { PaymentMethodIcon } from '../components/PaymentMethodIcon';
import { ClientActionComponent } from '../components/ClientActionComponent';
import { invoiceRowClassName } from '../utils/invoiceRowStatus';

export const Sales = ({ filter, pagination, refetchTurnovers }: TableInvoiceComponentProps & {
    refetchTurnovers: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [selectedItem, setSelectedItem] = useState<Invoice>();
    const [activeSubTab, setActiveSubTab] = useState<'sales' | 'sent'>('sales');

    // Подтабът пътува като част от филтъра, защото оттам минава към заявката - същото както при
    // задълженията. Изпратените се разпознават по състоянието на продажбата, не по нова колона.
    const activeFilter = {
        ...filter,
        sentByMail: activeSubTab === 'sent',
    };

    const [getInvoices, fetchNewInvoices] = useInvoiceData<Invoice[]>(useGetSales, pagination, {
        ...activeFilter,
    } as InvoiceFilter);

    const handleSubTabChange = (tab: 'sales' | 'sent') => {
        if (tab === activeSubTab) return;
        pagination.setCurrentPage(1);
        resourceCache(useGetSales).clear();
        setActiveSubTab(tab);
    };

    const refetchEntitiesHook = async () => {
        resourceCache(useGetSales).clear();
        resourceCache(fetchMapBranches).clear();
        await fetchNewInvoices({
            'pager.page': pagination.currentPage,
            'pager.itemsPerPage': pagination.pageSize,
            pagination,
            filter,
        });
        refetchTurnovers();
    };

    useEffect(() => {
        if (selectedItem !== undefined) {
            // И документният тип, не само id-то: сторнираната фактура дава два реда с едно и също id -
            // самата фактура и кредитното известие. Търсенето само по id връщаше първия, тоест избереш
            // ли известието, изборът веднага се подменяше с фактурата и прегледът показваше нея.
            setSelectedItem(() => getInvoices().paidInvoices.find(
                ({ id, documentType }) => id === selectedItem.id && documentType === selectedItem.documentType));
        }
    }, [getInvoices().paidInvoices]);

    return (
        <>
            <InvoiceView title={t('Details for') + " " + t(selectedItem?.documentType + "")} item={selectedItem}
                setSelectedItem={setSelectedItem} refetchEntitiesHook={refetchEntitiesHook} />
            <div className='d-flex border-bottom justify-content-center mb-3 p-3 px-1 w-100'>
                <div className='d-flex w-100 justify-content-between'>
                    <div
                        role='button'
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${activeSubTab === 'sales' ? 'active' : ''}`}
                        onClick={() => handleSubTabChange('sales')}
                    >
                        <h5>{t('allSales')}</h5>
                    </div>
                    <div
                        role='button'
                        className={`d-flex justify-content-center w-50 step-nav-link text-center ${activeSubTab === 'sent' ? 'active' : ''}`}
                        onClick={() => handleSubTabChange('sent')}
                    >
                        <h5>{t('sentEmails')}</h5>
                    </div>
                </div>
            </div>
            <ErrorBoundary fallback={<ErrorComponent errorText={t('invoices')} />}>
                <Suspense fallback={<Spinner animation='border' />}>
                    <InvoicesInner
                        filter={activeFilter}
                        pagination={pagination}
                        getEntities={getInvoices}
                        setSelectedItem={setSelectedItem}
                        activeSubTab={activeSubTab}
                        {...{ refetchEntitiesHook }}
                    />
                </Suspense>
            </ErrorBoundary>
        </>
    );
};

const InvoicesInner = ({
    filter,
    getEntities,
    pagination,
    refetchEntitiesHook,
    setSelectedItem,
    activeSubTab,
}: InvoiceTableInnerProps & { activeSubTab: 'sales' | 'sent' }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'invoice' });
    const { paidInvoices, pager } = getEntities();
    const isSentTab = activeSubTab === 'sent';
    const currentHeaders = isSentTab ? sentSalesTableHeaders : SalesTableHeaders;
    const [idOfEmailLoadingInvoice, setIdOfEmailLoadingInvoice] = useState<number>();
    const [multiMailLoading, setMultiMailLoading] = useState<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<Invoice[]>([]);
    // Кой ред не може да тръгне по мейл: вече изпратен; още непотвърден - фактура без номер няма какво да
    // носи в писмото; и редът на кредитното известие - той е втори изглед към същата продажба и същия
    // документ, така че избирането му праща фактурата втори път. Същото правило пази и бутона на реда
    // (disableEmail), затова стои на едно място.
    const cannotBeEmailed = ({ state, document, documentType }: Invoice) =>
        document?.invoiceNumber == undefined
        || documentType === 'creditnote'
        || documentType === 'storno'
        // На подтаба с изпратените изпращането е повторно и е разрешено - там смисълът е точно този.
        || (!isSentTab && state === 'sent by mail');
    const [nonSelectableItems, setNonSelectableItems] = useState<Invoice[]>(paidInvoices.filter(cannotBeEmailed));
    const [emailTarget, setEmailTarget] = useState<Invoice>();
    const [emailRecipients, setEmailRecipients] = useState<(string | EmailRecipientItem)[]>([]);
    const [emailRecipientStr, setEmailRecipientStr] = useState<string>();
    const [emailModalLoading, setEmailModalLoading] = useState<boolean>(false);
    const [loadingRecipient, setLoadingRecipient] = useState<boolean>(false);

    const [comment, setComment] = useState("");

    const useActionHook = async (fields: Invoice) => {
        await deleteDraftInvoiceSale(fields?.id, comment);
        refetchEntitiesHook();
        toast.success(<CustomToastContent content={[t('invoiceDeleted')]} />, toastOptionsTopRight);
    };

    const openEmailModal = async (fields: Invoice) => {
        setIdOfEmailLoadingInvoice(fields.id);
        setEmailTarget(fields);
        setEmailRecipients([]);
        setEmailRecipientStr(undefined);
        setLoadingRecipient(true);
        try {
            // Продажбата няма обект, значи няма и разделение обектни/кореспондентски адреси - идва само
            // имейлът на клиента.
            const { email, emails } = await getSaleEmailRecipient(fields.id ?? -1);
            setEmailRecipientStr(email || undefined);
            if (emails && emails.length > 0) {
                setEmailRecipients(emails);
            } else if (email) {
                setEmailRecipients(email.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean));
            } else {
                setEmailRecipients([]);
            }
        } catch (error) {
            showBackendError(error);
        } finally {
            setLoadingRecipient(false);
            setIdOfEmailLoadingInvoice(undefined);
        }
    };

    const confirmSendEmail = async () => {
        if (!emailTarget) return;
        setEmailModalLoading(true);
        try {
            const id = await emailSaleInvoice(emailTarget.id ?? -1, isSentTab);
            if (id) toast.success(<CustomToastContent content={[t('emailSuccess')]} />, toastOptionsTopRight);
            refetchEntitiesHook();
            setSelectedItems(prevState => prevState.filter(({ id }) => id !== emailTarget.id));
            // Плюс току-що изпратената: списъкът тук е отпреди презареждането, така че тя още не носи
            // "sent by mail". Условието беше обърнато - изключваше всички освен нея.
            setNonSelectableItems(paidInvoices.filter((invoice) => cannotBeEmailed(invoice) || invoice.id === emailTarget.id));
            setEmailTarget(undefined);
        } catch (error) {
            showBackendError(error);
            refetchEntitiesHook();
        } finally {
            setEmailModalLoading(false);
        }
    };
    const handleMultiSelectedSendEmail = async () => {
        setMultiMailLoading(true);
        try {
            const response = await emailSaleInvoices(selectedItems.map(({ id }) => id ?? -1));
            if (response) {
                toast.success(<CustomToastContent content={[t('emailSuccess')]} />, toastOptionsTopRight);
                refetchEntitiesHook();
                setSelectedItems(() => []);
                setNonSelectableItems(paidInvoices.filter(cannotBeEmailed));
            }
        } catch (error) {
            showBackendError(error);
            refetchEntitiesHook();
        } finally {
            setMultiMailLoading(false);
        }
    };

    useEffect(() => {
        setSelectedItems(() => []);
        setNonSelectableItems(paidInvoices.filter(cannotBeEmailed))
    }, [refetchEntitiesHook]);

    return (
        <>
            {paidInvoices.length > 0 ? (
                <>
                    <Suspense fallback={<Spinner animation='border' />}>
                        <ExportSales pagination={pagination} filter={filter}
                            fileName={t('invoices') + "-" + dateFormat(new Date(), date)}
                            wscols={SalesTableHeaders} />
                    </Suspense>
                    {!isSentTab && selectedItems.length > 0 && (multiMailLoading ? <Spinner animation={'border'} /> :
                        <CustomButton
                            className='w-30 mb-3'
                            onClick={handleMultiSelectedSendEmail}
                            title={t('emailInvoice')}
                        />
                    )}
                    <CustomTable
                        selectable={!isSentTab}
                        columns={{
                            headings: currentHeaders,
                            items: paidInvoices.map(
                                ({
                                    companyName,
                                    currency,
                                    eik,
                                    creationDate,
                                    documentType,
                                    user,
                                    paymentMethod,
                                    total,
                                    document,
                                    paymentProcessingStatus,
                                    invoiceNumber,
                                    emailSentDate,
                                }) => [
                                        // Редът на кредитното известие носи своя номер в invoiceNumber -
                                        // копиращият конструктор на InvoicingVM го слага там. Документът е
                                        // общ за двата реда, така че четенето само от него показваше
                                        // номера на фактурата и на двете места.
                                        invoiceNumber ?? document?.invoiceNumber,
                                        transformDateToString(creationDate),
                                        !documentType ? t('DRAFT') : t(documentType.toLowerCase()),
                                        !documentType ? (document?.clientName ?? companyName) : documentType.toLowerCase() == "receipt" ? t("privatePerson") : document?.clientName ?? companyName,
                                        !documentType ? '' : documentType.toLowerCase() == "receipt" ? "" : document?.eik ?? eik,
                                        user,
                                        `${transformPriceToString(total)}${getCurrencySymbol(currency)}`,
                                        <PaymentMethodIcon {...{ paymentMethod }} />,
                                        paymentProcessingStatus ? t(`status_${paymentProcessingStatus}`) : '-',
                                        ...(isSentTab
                                            ? [emailSentDate ? transformDateToString(emailSentDate, 'dd/mm/yyyy HH:MM') : '']
                                            : []),
                                    ],
                            ),
                        }}
                        viewComponentObject={(invoice) => {
                            const item = paidInvoices.find((value) => value.id === invoice.id && value.documentType === invoice.documentType);
                            setSelectedItem(item);
                        }}
                        accessEditRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        accessActionRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                        editComponent={
                            ({ itemToEdit, setShowModal }: EditProps<Invoice>) =>
                                <EditDocument itemToEdit={itemToEdit} setShowModal={setShowModal}
                                    invoices={paidInvoices}
                                    refetchEntitiesHook={refetchEntitiesHook} />
                        }
                        actionHook={
                            filter.paid === InvoicePaymentStatus.Unpaid
                                ? useActionHook
                                : undefined
                        }
                        comment={comment}
                        setComment={setComment}
                        sendEmailHook={openEmailModal}
                        rowClassName={invoiceRowClassName}
                        rowExtraAction={(invoice) => (
                            // Потвърждението и сторното стоят в диалога на фактурата, пред самия документ,
                            // върху който се решават. Тук остава само пътят към клиента.
                            <ClientActionComponent
                                // Името от продажбата, не от документа: document.clientName е снимка от
                                // момента на издаване и след преименуване на клиента търсенето по нея не
                                // намира нищо.
                                companyName={invoice.companyName}
                                accessEditRoles={[Role.FINANCIAL, Role.COORDINATOR]}
                                {...{ refetchEntitiesHook }}
                            />
                        )}
                        tableData={paidInvoices}
                        {...{ refetchEntitiesHook }}
                        setSelectedItems={!isSentTab ? setSelectedItems : undefined}
                        disableEmail={cannotBeEmailed}
                        notSelectable={nonSelectableItems}
                        idOfInvoiceEmailThatIsLoading={idOfEmailLoadingInvoice}
                    />
                    <PaginationComponent recordsCount={pager.totalCount} pagination={pagination} />
                    <SendInvoiceEmailModal
                        show={emailTarget !== undefined}
                        isResend={isSentTab}
                        lastSentAt={emailTarget?.emailSentDate
                            ? transformDateToString(emailTarget.emailSentDate, 'dd/mm/yyyy HH:MM')
                            : undefined}
                        invoiceLabel={`${t('invoice')} № ${emailTarget?.document?.invoiceNumber ?? ''} - ${emailTarget?.companyName ?? ''}`}
                        recipient={emailRecipientStr}
                        recipients={emailRecipients}
                        loading={emailModalLoading}
                        loadingRecipient={loadingRecipient}
                        onConfirm={confirmSendEmail}
                        onCancel={() => setEmailTarget(undefined)}
                    />
                </>
            ) : (
                <NoDataComponent noDataText={t('invoices')}></NoDataComponent>
            )}
        </>
    );
};
